import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  listDiscoveredMachineHealthSensors,
  previewEdgeConnector,
  renameDiscoveredMachineHealthSensor,
  retireDiscoveredMachineHealthSensor,
} from "../../../api";
import chevronIcon from "../../../assets/chevron-right.svg";
import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import { connectorValidationMessage } from "./EdgeConnectorReview";
import { serialPortLabel } from "./EdgeModbusDeviceForm";
import { buildNCDGen4EthernetConnectorSpec, buildNCDGen4USBConnectorSpec } from "./edge-connector-spec";
import { MACHINE_HEALTH_URL } from "../constants";

const machineHealthURLForSensor = (displayName) => {
  const name = String(displayName || "").trim();
  if (!name) return MACHINE_HEALTH_URL;
  const separator = MACHINE_HEALTH_URL.includes("?") ? "&" : "?";
  return `${MACHINE_HEALTH_URL}${separator}sensor=${encodeURIComponent(name)}`;
};

const isNamedNCDReceiverPort = (path) => /(?:^|\/)usb-ncd\.io_/i.test(String(path));

// The radio address is printed on every NCD sensor's enclosure label; grouped
// in fours it can be read off the sticker to tell same-looking units apart.
const radioIDLabel = (deviceID) => {
  const hex = String(deviceID || "").trim().toUpperCase();
  if (!/^[0-9A-F]{16}$/.test(hex)) return "";
  return hex.replace(/(.{4})(?=.)/g, "$1 ");
};

const receiverPort = (receiverPorts, preferred = "") => {
  // An edit must never jump to a different USB device merely because the
  // saved receiver is temporarily unplugged. Keep the exact saved path and
  // make the operator reconnect it before review.
  if (preferred) return preferred;
  const identified = receiverPorts.filter(isNamedNCDReceiverPort);
  if (identified.length === 1) return identified[0];
  // An unrecognized serial device may be a console, field bus, or modem. Never
  // guess which physical port the receiver owns; the operator must choose it.
  return "";
};

const initialReceiverTransport = (proposed, receiverPorts, ethernetSupported, usbSupported) => {
  if (proposed.transport) return proposed.transport;
  const identified = receiverPorts.filter(isNamedNCDReceiverPort);
  if (usbSupported && identified.length === 1) return "usb";
  if (ethernetSupported) return "ethernet";
  return "usb";
};

const publicError = (error, gatewayName) => {
  const validation = connectorValidationMessage(error);
  if (validation) return validation;
  const text = String(error?.message || "").toLowerCase();
  if (/changed since|out of date|revision conflict|refresh .*try again/.test(text)) {
    return "This connection changed after you opened it. Refresh the page and review your changes again.";
  }
  if (/already (used|claimed|reserved|connected)|resource conflict|in use by/.test(text)) {
    return "That receiver is already used by another connection. Edit or remove the existing connection.";
  }
  if (/selected usb receiver.+not available|receiver is not available/.test(text)) {
    return `The selected USB receiver is no longer available on ${gatewayName}. Reconnect it, refresh, and try again.`;
  }
  if (/sensor no longer exists|sensor is disabled|sensor settings need attention|identity does not match|available machine|disabled machine/.test(text)) {
    return "This sensor setup changed. Return to Machine Health, check the sensor identity, then try again.";
  }
  if (/offline|not online|unreachable|not connected/.test(text)) {
    return `${gatewayName} is offline or unreachable. Reconnect it and try again.`;
  }
  return "Control Seat could not prepare this sensor connection. Check the receiver and sensor setup, then try again.";
};

const lastReportLabel = (value, reportingState) => {
	const time = Date.parse(value || "");
	if (!Number.isFinite(time)) return "Waiting for a report";
	const suffix = reportingState === "late" ? " · Not reporting" : "";
	const seconds = Math.max(0, Math.round((Date.now() - time) / 1000));
	if (seconds < 90) return `Reporting now${suffix}`;
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return `Last report ${minutes}m ago${suffix}`;
	const hours = Math.round(minutes / 60);
	if (hours < 48) return `Last report ${hours}h ago${suffix}`;
	const days = Math.round(hours / 24);
	return `Last report ${days}d ago${suffix}`;
};

const sensorActionError = (error, fallback = "This sensor could not be updated. Refresh and try again.") => {
  const message = String(error?.message || "");
  try {
    const parsed = JSON.parse(message);
    if (typeof parsed?.error === "string" && parsed.error.trim()) return parsed.error;
  } catch (_) {
    // The shared API helper returns plain text for non-JSON failures.
  }
  return message || fallback;
};

const SENSOR_REFRESH_MS = 15_000;

/**
 * Commission the receive-only NCD Ethernet or USB modem. Sensor discovery is deliberately
 * not configuration: reports create managed, read-only tags automatically and
 * the later sensor-to-machine association belongs to Machine Health.
 */
export default function EdgeNCDSensorForm({
  gateway,
  capabilities = [],
  ncdUSBReceiverPorts = [],
  existingConnectorNames = [],
  initialDraft = null,
  busy = false,
  notify,
  onCancel,
  onPreview,
}) {
  const proposed = initialDraft?.settings && typeof initialDraft.settings === "object" ? initialDraft.settings : {};
  const capabilitySet = useMemo(() => new Set(Array.isArray(capabilities) ? capabilities : []), [capabilities]);
  const ethernetSupported = capabilitySet.has("ncd-pr63-27c-ethernet-receiver-v1");
  const usbSupported = gateway.hardware_profile !== "generic-linux-ipc-v1" &&
    capabilitySet.has("ncd-pr63-27c-usb-receiver-v1");
  const existingGenericUSB = gateway.hardware_profile === "generic-linux-ipc-v1" &&
    Boolean(initialDraft?.connector_id) && String(proposed.transport || "usb") === "usb";
  const usbVisible = usbSupported || existingGenericUSB;
  const [transport, setTransport] = useState(() => (
    initialReceiverTransport(proposed, ncdUSBReceiverPorts, ethernetSupported, usbVisible)
  ));
  const [port, setPort] = useState(() => receiverPort(ncdUSBReceiverPorts, proposed.serialPort));
  const [baudRate, setBaudRate] = useState(proposed.baudRate || 115200);
  const [autoDiscover, setAutoDiscover] = useState(() => (
    typeof proposed.autoDiscover === "boolean" ? proposed.autoDiscover : !proposed.host
  ));
  const [host, setHost] = useState(proposed.host || "192.168.77.16");
  const [ethernetPort, setEthernetPort] = useState(proposed.port || 2101);
  const [apiMode, setAPIMode] = useState(String(proposed.apiMode || 1));
  const [receiverIdentityConfirmed, setReceiverIdentityConfirmed] = useState(() => (
    Boolean(initialDraft?.connector_id) && !isNamedNCDReceiverPort(proposed.serialPort)
  ));
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState("");
  const [sensors, setSensors] = useState([]);
  const [sensorsLoading, setSensorsLoading] = useState(false);
  const [sensorRefreshError, setSensorRefreshError] = useState("");
  const [sensorActionErrorText, setSensorActionErrorText] = useState("");
  const [renamingSensor, setRenamingSensor] = useState("");
  const [sensorName, setSensorName] = useState("");
  const [sensorExpectedName, setSensorExpectedName] = useState("");
  const [savingSensorName, setSavingSensorName] = useState(false);
  const [retireCandidate, setRetireCandidate] = useState("");
  const [retiringSensor, setRetiringSensor] = useState("");
  const errorRef = useRef(null);
  const sensorListMutationRef = useRef(0);

  useEffect(() => {
    setPort((current) => receiverPort(ncdUSBReceiverPorts, current || proposed.serialPort));
  }, [ncdUSBReceiverPorts, proposed.serialPort]);

  useEffect(() => {
    if (transport === "ethernet" && !ethernetSupported && usbVisible) setTransport("usb");
    if (transport === "usb" && !usbVisible && ethernetSupported) setTransport("ethernet");
  }, [ethernetSupported, transport, usbVisible]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const connectorID = String(initialDraft?.connector_id || "");
  useEffect(() => {
    if (!connectorID) {
      setSensors([]);
      setSensorsLoading(false);
      setSensorRefreshError("");
      setSensorActionErrorText("");
      return undefined;
    }

    let activeController = null;
    let disposed = false;
    setSensors([]);
    setSensorsLoading(true);
    setSensorRefreshError("");
    setSensorActionErrorText("");
    setRenamingSensor("");
    setSensorExpectedName("");
    setRetireCandidate("");

    const loadSensors = async () => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      const mutationAtStart = sensorListMutationRef.current;
      try {
        const payload = await listDiscoveredMachineHealthSensors({
          gatewayId: gateway.id,
          connectorId: connectorID,
          signal: controller.signal,
        });
        if (disposed || controller.signal.aborted || mutationAtStart !== sensorListMutationRef.current) return;
        const rows = Array.isArray(payload?.sensors) ? payload.sensors : [];
        setSensors(rows.filter((sensor) => (
          sensor?.gateway_id === gateway.id && sensor?.connector_id === connectorID && sensor?.sensor_key
        )));
        setSensorRefreshError("");
      } catch (loadError) {
        if (!disposed && loadError?.name !== "AbortError") {
          // A background failure never erases the last useful list. The next
          // poll retries automatically, while the inline message makes the
          // stale state explicit to the operator.
          setSensorRefreshError("Detected sensors could not be refreshed. Control Seat will try again automatically.");
        }
      } finally {
        if (!disposed && !controller.signal.aborted) setSensorsLoading(false);
      }
    };

    loadSensors();
    const timer = window.setInterval(loadSensors, SENSOR_REFRESH_MS);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      activeController?.abort();
    };
  }, [connectorID, gateway.id]);

  const receiverCandidates = useMemo(() => (
    Array.isArray(ncdUSBReceiverPorts) ? ncdUSBReceiverPorts.filter(Boolean) : []
  ), [ncdUSBReceiverPorts]);
  const portDetected = Boolean(port && receiverCandidates.includes(port));
  const receiverNeedsConfirmation = portDetected && !isNamedNCDReceiverPort(port);
  const receiverReady = transport === "ethernet"
    ? (autoDiscover || Boolean(String(host || "").trim()))
    : !existingGenericUSB && portDetected && (!receiverNeedsConfirmation || receiverIdentityConfirmed);
  const transportOptions = useMemo(() => [
    ...(ethernetSupported ? [{ value: "ethernet", label: "Ethernet receiver" }] : []),
    ...(usbVisible ? [{ value: "usb", label: existingGenericUSB ? "Existing USB receiver" : "USB receiver" }] : []),
  ], [ethernetSupported, existingGenericUSB, usbVisible]);
  const portOptions = useMemo(() => {
    const paths = [...receiverCandidates];
    if (port && !paths.includes(port)) paths.unshift(port);
    return paths.map((path) => ({
      value: path,
      label: `${serialPortLabel(path, gateway.hardware_profile)}${
        receiverCandidates.includes(path)
            ? (!isNamedNCDReceiverPort(path) ? " (confirm before use)" : "")
          : existingGenericUSB && path === port
            ? " (existing connection)"
          : " (not detected)"
      }`,
    }));
  }, [existingGenericUSB, gateway.hardware_profile, port, receiverCandidates]);

  // A site with more than one receiver otherwise gets identically named
  // connections. Number from the existing list so each row stays tellable
  // apart; an edit always keeps the connection's saved name.
  const defaultReceiverName = useMemo(() => {
    const taken = new Set(
      (Array.isArray(existingConnectorNames) ? existingConnectorNames : [])
        .map((name) => String(name || "").trim().toLowerCase())
        .filter(Boolean),
    );
    const base = "Machine health receiver";
    if (!taken.has(base.toLowerCase())) return base;
    let number = 2;
    while (taken.has(`${base.toLowerCase()} ${number}`)) number += 1;
    return `${base} ${number}`;
  }, [existingConnectorNames]);

  const review = async () => {
    setError("");
    let spec;
    try {
      const common = {
        connectorID: initialDraft?.connector_id || "",
        desiredState: initialDraft?.desired_state || "running",
        name: initialDraft?.name || defaultReceiverName,
        apiMode,
      };
      spec = transport === "ethernet"
        ? buildNCDGen4EthernetConnectorSpec({ ...common, host, port: ethernetPort, autoDiscover })
        : buildNCDGen4USBConnectorSpec({ ...common, serialPort: port, baudRate });
    } catch (buildError) {
      setError(buildError.message);
      return;
    }
    setReviewing(true);
    try {
      const preview = await previewEdgeConnector(gateway.id, spec, initialDraft?.expected_revision);
      onPreview?.({
        ...preview,
        sensor_receiver: {
          transport,
          port_label: transport === "ethernet"
            ? (autoDiscover ? "Find automatically on the local network" : `${String(host).trim()}:${Number(ethernetPort)}`)
            : serialPortLabel(port, gateway.hardware_profile),
          ...(transport === "ethernet" ? { network_discovery: autoDiscover } : {}),
          auto_discovery: true,
        },
      }, null);
    } catch (previewError) {
      const message = publicError(previewError, gateway.name);
      setError(message);
      notify?.({ type: "error", title: "Sensor connection could not be prepared", message });
    } finally {
      setReviewing(false);
    }
  };

  const saveSensorName = async (sensor) => {
    const next = sensorName.trim();
    const expectedName = sensorExpectedName.trim();
    if (!next) {
      setSensorActionErrorText("Enter the name operators use for this sensor.");
      return;
    }
    if (next === expectedName) {
      setRenamingSensor("");
      setSensorExpectedName("");
      setSensorActionErrorText("");
      return;
    }
    setSavingSensorName(true);
    setSensorActionErrorText("");
    try {
      const saved = await renameDiscoveredMachineHealthSensor(sensor.sensor_key, next, expectedName);
      // Ignore a list response that began before this mutation. Otherwise a
      // slow refresh can briefly put the old name back and make the next edit
      // submit an already-stale expected name.
      sensorListMutationRef.current += 1;
      setSensors((current) => current.map((item) => (
        item.sensor_key === sensor.sensor_key ? { ...item, ...saved } : item
      )));
      setRenamingSensor("");
      setSensorExpectedName("");
      notify?.({ type: "success", title: "Sensor renamed", message: "The sensor name was saved. Its generated tag folder updates automatically." });
    } catch (renameError) {
      setSensorActionErrorText(sensorActionError(renameError, "This sensor could not be renamed. Refresh and try again."));
    } finally {
      setSavingSensorName(false);
    }
  };

  const retireSensor = async (sensor) => {
    setRetiringSensor(sensor.sensor_key);
    setSensorActionErrorText("");
    try {
		await retireDiscoveredMachineHealthSensor(sensor.sensor_key, sensor.gateway_id, sensor.connector_id);
      // Fence a list response that began before retirement, then remove the row
      // immediately. A later physical report may legitimately add it again.
      sensorListMutationRef.current += 1;
      setSensors((current) => current.filter((item) => item.sensor_key !== sensor.sensor_key));
      setRetireCandidate("");
      notify?.({
        type: "success",
        title: "Sensor retired",
        message: "Its tags and history remain available. It will return here if it reports again.",
      });
    } catch (retireError) {
      setSensorActionErrorText(sensorActionError(retireError, "This sensor could not be retired. Refresh and try again."));
    } finally {
      setRetiringSensor("");
    }
  };

  const locked = busy || reviewing || savingSensorName || Boolean(retiringSensor);
  // The connection fields render in two placements: as the primary content
  // while creating, and collapsed under the sensor list once the receiver
  // exists — a working receiver's day-two workflow is naming sensors, not
  // re-reading transport settings.
  const receiverConnectionGroup = (
      <div className="gwConnectionFormGroup">
        <div className="gwLabel">Receiver</div>
        <div className="gwHint">Attach the antenna and connect the NCD receiver. Control Seat listens for compatible sensors and creates their tags automatically.</div>
        <div className="gwConnectionFields gwNCDReceiverFields">
          {transportOptions.length > 1 ? (
            <div className="gwField">
              <label className="gwLabel">Connection</label>
              <Select
                value={transport}
                disabled={locked}
                options={transportOptions}
                onChange={(value) => { setTransport(value); setError(""); }}
              />
              <small className="gwHint">Choose how the receiver is physically connected to {gateway.name}.</small>
            </div>
          ) : null}
          {transport === "ethernet" ? (
            <>
              <div className="gwField">
                <label className="gwLabel">Find receiver</label>
                <Select
                  value={autoDiscover ? "automatic" : "address"}
                  disabled={locked}
                  options={[
                    { value: "automatic", label: "Automatically (recommended)" },
                    { value: "address", label: "Use a fixed address" },
                  ]}
                  onChange={(value) => { setAutoDiscover(value === "automatic"); setError(""); }}
                />
                {autoDiscover ? (
                  <small className="gwHint">Automatic setup listens for one NCD receiver on the local network. Discovery can take about 30 seconds.</small>
                ) : null}
              </div>
              {!autoDiscover ? (
                <div className="gwField">
                  <label className="gwLabel">Receiver address</label>
                  <input
                    className="gwInput"
                    value={host}
                    disabled={locked}
                    placeholder="192.168.77.16"
                    inputMode="decimal"
                    onChange={(event) => { setHost(event.target.value); setError(""); }}
                  />
                  <small className="gwHint">Use a private static address when the site has more than one NCD Ethernet receiver.</small>
                </div>
              ) : null}
              <div className="gwNotice isSoft gwConnectionWideField gwNCDNetworkGuidance" role="note">
                {autoDiscover ? (
                  <div>
                    <strong>Recommended: use the same router or switch</strong>
                    <span>Connect 12 VDC power and the antenna, then connect the receiver by Ethernet to the network used by {gateway.name}. The gateway can stay on Wi-Fi. With DHCP, Control Seat finds the receiver automatically.</span>
                  </div>
                ) : (
                  <div>
                    <strong>Using a direct cable?</strong>
                    <span>Connect 12 VDC power and the antenna. A site technician must first give the receiver and a dedicated gateway Ethernet port compatible private addresses. Control Seat does not change network settings.</span>
                  </div>
                )}
              </div>
            </>
          ) : (
          <div className="gwField">
            <label className="gwLabel">Serial port</label>
            {portOptions.length ? (
              <Select
                value={port}
                disabled={locked || existingGenericUSB}
                options={portOptions}
                onChange={(value) => { setPort(value); setReceiverIdentityConfirmed(false); setError(""); }}
              />
            ) : (
              <input className="gwInput" value="No USB receiver detected" readOnly aria-readonly="true" />
            )}
            <small className={`gwHint ${portDetected ? "isPositive" : "isWarning"}`} role="status">
              {existingGenericUSB
                ? initialDraft?.desired_state === "running"
                  ? "Retained while this connection keeps running. Move to Ethernet before changing it or turning it off."
                  : "This stopped connection cannot be restarted over USB. Move to Ethernet to reconnect it."
                : receiverNeedsConfirmation
                ? "A supported USB serial receiver was detected, but its device path does not identify it as NCD hardware. Confirm the receiver you connected."
                : portDetected
                  ? "Detected on this device. A stable device path will keep working after a reboot."
                : port
                  ? `The saved USB receiver is not detected. Reconnect it to ${gateway.name}, wait a few seconds, then refresh this page.`
                  : `Connect the USB receiver to ${gateway.name} with a data cable, wait a few seconds, then refresh this page.`}
            </small>
            {receiverNeedsConfirmation ? (
              <label className="gwCheckboxRow">
                <input
                  type="checkbox"
                  checked={receiverIdentityConfirmed}
                  disabled={locked}
                  onChange={(event) => { setReceiverIdentityConfirmed(event.target.checked); setError(""); }}
                />
                <span>This is the NCD receiver I connected</span>
              </label>
            ) : null}
          </div>
          )}
        </div>
      </div>
  );
  return (
    <>
      {error ? (
        <div className="gwAlert" role="alert" tabIndex={-1} ref={errorRef}>
          <div className="gwAlertTitle">Check the sensor connection</div>
          <div className="gwAlertBody">{error}</div>
        </div>
      ) : null}
      {existingGenericUSB && transport === "usb" ? (
        <div className="gwNotice isSoft gwConnectionWideField" role="note">
          {initialDraft?.desired_state === "running"
            ? "This USB connection can keep running, but it cannot be changed or restarted over USB on this device. Move it to Ethernet before changing it or turning it off."
            : "This USB connection is stopped and cannot be turned back on over USB on this device. Move it to Ethernet to reconnect the receiver."}
        </div>
      ) : null}
      {!connectorID ? receiverConnectionGroup : null}
      {!connectorID ? (
        <div className="gwNotice gwConnectionInlineNotice">
          <div>
            <strong>Sensors are added automatically</strong>
            <span>When a compatible sensor reports, Control Seat creates a read-only tag folder for it. You can use those tags anywhere, then attach the sensor to a machine in Machine Health.</span>
          </div>
        </div>
      ) : null}

      {connectorID ? (
        <div className="gwConnectionFormGroup">
          <div className="gwLabel">Detected sensors</div>
          <div className="gwHint">Name each sensor after its machine or measurement point. The exact generated tag folder is shown below.</div>
          {sensorActionErrorText || sensorRefreshError ? <div className="gwAlert" role="alert"><div className="gwAlertBody">{sensorActionErrorText || sensorRefreshError}</div></div> : null}
          {sensorsLoading ? <div className="gwHint" role="status">Loading detected sensors…</div> : null}
          {!sensorsLoading && !sensorRefreshError && sensors.length === 0 ? (
            <div className="gwHint">No compatible sensor report yet. Check that the sensor is powered, in range, and uses the same radio network. Control Seat will keep listening.</div>
          ) : null}
          {sensors.length ? (
            <div className="gwNCDSensorList">
              {sensors.map((sensor) => {
                const editing = renamingSensor === sensor.sensor_key;
                const confirmingRetirement = retireCandidate === sensor.sensor_key;
                const displayName = String(sensor.display_name || "Sensor");
                const radioID = radioIDLabel(sensor.device_id);
                // A still-auto-named sensor ("Sensor 3", legacy "NCD sensor 3")
                // is anonymous when several units arrive together; its radio ID
                // matches the address printed on the physical sensor's label.
                const autoNamed = /^(?:NCD sensor|Sensor)(?: \d+)?$/.test(displayName);
                const tagStatus = sensor?.tag_status && typeof sensor.tag_status === "object" ? sensor.tag_status : null;
                const tagNeedsAttention = tagStatus?.state && tagStatus.state !== "ready";
                const generatedFolderPath = String(tagStatus?.folder_path || "").trim();
                const reportingLate = sensor.reporting_state === "late";
                const waitingForSettings = sensor.settings_ready === false || tagStatus?.state === "waiting_for_settings";
                const setupLabel = !sensor.settings_ready
                  ? reportingLate ? "Not reporting · Waiting for sensor settings" : "Connected · Waiting for sensor settings"
                  : tagStatus?.state === "ready"
                    ? "Ready to attach in Machine Health"
                    : tagStatus?.state === "needs_attention"
                      ? "Connected · Tag setup needs attention"
                      : "Connected · Waiting for complete sensor data";
                const tagStatusMessage = reportingLate && waitingForSettings
                  ? "This sensor is not reporting, so its settings cannot be confirmed yet."
                  : tagStatus?.message;
                return (
                  <div className="gwEdgeProgramRow" key={sensor.sensor_key}>
                    <div className="gwEdgeProgramMain">
                      {editing ? (
                        <label className="gwField">
                          <span className="gwLabel">Sensor name</span>
                          <input
                            className="gwInput"
                            value={sensorName}
                            maxLength={120}
                            autoFocus
                            disabled={savingSensorName}
                            onChange={(event) => { setSensorName(event.target.value); setSensorActionErrorText(""); }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                saveSensorName(sensor);
                              }
                              if (event.key === "Escape") {
                                setRenamingSensor("");
                                setSensorExpectedName("");
                                setSensorActionErrorText("");
                              }
                            }}
                          />
                          {radioID ? <small className="gwHint">Radio ID {radioID} — printed on the sensor’s label</small> : null}
                        </label>
                      ) : (
                        <>
                          <span className="gwListTitle" title={radioID ? `Radio ID ${radioID}` : undefined}>{displayName}</span>
                          {autoNamed && radioID ? (
                            <div className="gwListMeta">Radio ID {radioID} — printed on the sensor’s label</div>
                          ) : null}
                          <div className="gwListMeta">
							{sensor.asset_name ? `Monitoring ${sensor.asset_name}` : setupLabel} · {lastReportLabel(sensor.last_seen_at, reportingLate && !sensor.asset_name && !sensor.settings_ready ? "" : sensor.reporting_state)}
                          </div>
                          {generatedFolderPath ? <div className="gwListMeta">Tags: {generatedFolderPath}</div> : null}
                          {tagNeedsAttention ? (
                            <div className="gwNCDSensorTagStatus" role="status">
                              <span>{tagStatusMessage}</span>
                              {tagStatus.recovery_action ? <small>{tagStatus.recovery_action}</small> : null}
                            </div>
                          ) : null}
                          {confirmingRetirement ? (
                            <div className="gwNCDSensorTagStatus" role="note">
                              <span>Retire {displayName}?</span>
                              <small>Its tags and history stay available. It will return if it reports again.</small>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                    <div className="gwRowActions">
                      {editing ? (
                        <>
                          <button type="button" className="gwGhostBtn" disabled={savingSensorName} onClick={() => { setRenamingSensor(""); setSensorExpectedName(""); setSensorActionErrorText(""); }}>Cancel</button>
                          <button type="button" className="gwPrimaryBtn" disabled={savingSensorName || !sensorName.trim()} onClick={() => saveSensorName(sensor)}>
                            {savingSensorName ? "Saving…" : "Save name"}
                          </button>
                        </>
                      ) : confirmingRetirement ? (
                        <>
                          <button type="button" className="gwGhostBtn" disabled={Boolean(retiringSensor)} onClick={() => { setRetireCandidate(""); setSensorActionErrorText(""); }}>Cancel</button>
                          <button type="button" className="gwDangerBtn" disabled={Boolean(retiringSensor)} onClick={() => retireSensor(sensor)}>
                            {retiringSensor === sensor.sensor_key ? "Retiring…" : "Retire sensor"}
                          </button>
                        </>
                      ) : (
                        <>
                          {!sensor.asset_name ? <a className="gwRowAction" href={machineHealthURLForSensor(displayName)}>Attach to machine</a> : null}
                          <button type="button" className="gwRowAction" disabled={locked} onClick={() => { setRetireCandidate(""); setRenamingSensor(sensor.sensor_key); setSensorName(displayName); setSensorExpectedName(displayName); setSensorActionErrorText(""); }}>
                            Rename
                          </button>
                          {!sensor.asset_name ? (
                            <button type="button" className="gwRowAction isDanger" disabled={locked} onClick={() => { setRenamingSensor(""); setRetireCandidate(sensor.sensor_key); setSensorActionErrorText(""); }}>
                              Retire
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {connectorID ? (
        <details
          className="gwConnectionAdvanced"
          open={!receiverReady || Boolean(error) || undefined}
        >
          <summary><img src={chevronIcon} alt="" className="gwIcon gwDisclosureChevron" />Receiver connection</summary>
          {receiverConnectionGroup}
        </details>
      ) : null}

      <details className="gwConnectionAdvanced">
        <summary><img src={chevronIcon} alt="" className="gwIcon gwDisclosureChevron" />Receiver settings</summary>
        <div className="gwConnectionFields">
          {transport === "usb" ? (
            <div className="gwField">
              <label className="gwLabel">Baud rate</label>
              <NudgeNumber value={baudRate} onChange={setBaudRate} min={1200} max={4000000} disabled={locked || existingGenericUSB} aria-label="NCD receiver baud rate" />
              <small className="gwHint">The NCD USB receiver normally uses 115200.</small>
            </div>
          ) : (
            <div className="gwField">
              <label className="gwLabel">Receiver port</label>
              <NudgeNumber value={ethernetPort} onChange={setEthernetPort} min={1} max={65535} disabled={locked} aria-label="NCD Ethernet receiver port" />
              <small className="gwHint">NCD Ethernet receivers normally use port 2101.</small>
            </div>
          )}
          <div className="gwField">
            <label className="gwLabel">Radio API mode</label>
            <Select
              value={apiMode}
              disabled={locked || (existingGenericUSB && transport === "usb")}
              options={[{ value: "1", label: "API mode 1 (standard)" }, { value: "2", label: "API mode 2 (escaped)" }]}
              onChange={setAPIMode}
            />
            <small className="gwHint">Use mode 1 unless the modem was deliberately configured for escaped frames.</small>
          </div>
        </div>
      </details>

      <div className="gwHint">Connect checks these settings, then sends the connection to {gateway.name}.</div>
      <footer className="gwConnectionBuilderFooter">
        <span />
        <div className="gwHeaderActions">
          <button type="button" className="gwGhostBtn" disabled={locked} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="gwPrimaryBtn"
            disabled={locked || !receiverReady}
            onClick={review}
          >
            {reviewing || busy
              ? (initialDraft?.connector_id ? "Saving…" : "Connecting…")
              : initialDraft?.connector_id ? "Save changes" : "Connect"}
          </button>
        </div>
      </footer>
    </>
  );
}
